// src/components/backoffice/articles/ArticlePreviewPage.tsx
//
// תצוגה מקדימה של מאמר בתוך הבקאופיס, כולל טיוטות שלא פורסמו.
// ה-API הציבורי מחזיר מאמרים רק כש-is_published = 1, ולכן אין דרך לראות
// טיוטה באתר עצמו. הדף הזה נשען על endpoint הבקאופיס (מוגן בסשן) ומשחזר
// את מבנה עמוד המאמר הציבורי.
//
// גוף התוכן מרונדר בדיוק כמו בעמוד הציבורי — אותו ReactMarkdown, אותה
// רשימת תוספים מ-utils/articleMarkdown ואותה מחלקת .article-content —
// כדי שהתצוגה לא תוכל להראות משהו אחר ממה שיעלה לאתר.

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ARTICLE_REMARK_PLUGINS } from "../../../utils/articleMarkdown";
import { articlesService, Article } from "../../../services/backoffice/articlesService";

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "טרם פורסם";
  return new Date(dateStr).toLocaleDateString("he-IL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const ArticlePreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadArticle = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const data = await articlesService.getArticles({ id });
      const found = data.articles?.[0];
      if (found) {
        setArticle(found);
      } else {
        setError("המאמר לא נמצא");
      }
    } catch (err: any) {
      setError(`שגיאה בטעינת המאמר: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadArticle(); }, [loadArticle]);

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center text-gray-500">
        טוען תצוגה מקדימה...
      </div>
    );
  }

  if (error || !article) {
    return (
      <div dir="rtl" className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold text-gray-700">{error || "המאמר לא נמצא"}</h1>
        <Link to="/backoffice/articles" className="text-blue-700 hover:underline">
          חזרה לניהול מאמרים
        </Link>
      </div>
    );
  }

  const isPublished = !!article.is_published;

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50">
      {/* סרגל תצוגה מקדימה — לא חלק מהעמוד באתר */}
      <div className="bg-gray-900 text-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium">תצוגה מקדימה</span>
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                isPublished ? "bg-green-500/20 text-green-300" : "bg-amber-500/20 text-amber-300"
              }`}
            >
              {isPublished ? "מפורסם" : "טיוטה — לא גלוי באתר"}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {isPublished && article.slug && (
              <a
                href={`/articles/${article.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white hover:underline"
              >
                פתח באתר
              </a>
            )}
            <button
              onClick={() => navigate(`/backoffice/articles/edit/${article.id}`)}
              className="text-gray-300 hover:text-white hover:underline"
            >
              עריכה
            </button>
            <button
              onClick={() => navigate("/backoffice/articles")}
              className="text-gray-300 hover:text-white hover:underline"
            >
              לרשימה
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="bg-blue-900 text-white text-xs font-medium px-3 py-1 rounded-full">
              {article.category_name || "ללא קטגוריה"}
            </span>
            <span className="text-gray-400 text-sm">{formatDate(article.published_at)}</span>
            <span className="text-gray-400 text-sm">
              ⏱ {article.read_time_minutes} דקות קריאה
            </span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
            {article.title}
          </h1>

          {article.cover_image_url && (
            <img
              src={article.cover_image_url}
              alt={article.title}
              className="w-full rounded-lg mb-8"
            />
          )}

          <div className="article-content">
            <ReactMarkdown remarkPlugins={ARTICLE_REMARK_PLUGINS}>
              {article.content || ""}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlePreviewPage;
